// frontend/src/pages/CatalogoMateriaisServicos/GerenciarCatalogoMateriaisServicos.jsx

import React, { useState, useEffect } from "react";
import Template from "../../../components/Template";
import api from "../../../services/api";
import ConfirmationModal from "../../../components/ConfirmationModal";
import SuccessModal from "../../../components/SuccessModal";
import { useNavigate } from "react-router-dom";
import usePermission from "../../../hooks/usePermission";

const GerenciarCatalogoMateriaisServicos = () => {
    const navigate = useNavigate();

    // Lista de itens do catálogo
    const [catalogoList, setCatalogoList] = useState([]);

    // Formulário
    const [formData, setFormData] = useState({
        id: null,
        codigoNcm: "",
        descricao: "",
        tipo: "Material",
        unidadeMedida: "Unidade",
    });

    // Estados para controle do formulário
    const [isEditMode, setIsEditMode] = useState(false);

    const podeCriar = usePermission("CRIAR_CONFIGURACAO_MENU_SUSPENSO_CATALOGO");
    const podeAtualizar = usePermission("ATUALIZAR_CONFIGURACAO_MENU_SUSPENSO_CATALOGO");
    const podeDeletar = usePermission("DELETAR_CONFIGURACAO_MENU_SUSPENSO_CATALOGO");


    // Modais
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [showConfirmDelete, setShowConfirmDelete] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);
    const [showSuccessDialog, setShowSuccessDialog] = useState(false);

    // Unidades de Medida disponíveis
    const unidadesMedida = [
        "Hora",
        "Unidade",
        "m³",
        "km",
        "Kg",
        "Litro",
        // Adicione outras unidades conforme necessário
    ];

    // Tipos disponíveis
    const tipos = ["Material", "Serviço"];

    // Carregar lista do catálogo
    useEffect(() => {
        fetchCatalogoList();
    }, []);

    const fetchCatalogoList = async () => {
        try {
            const response = await api.get("/catalogos");
            setCatalogoList(response.data);
        } catch (error) {
            console.error("Erro ao carregar catálogo:", error);
            alert("Erro ao carregar catálogo. Tente novamente.");
        }
    };

    // Manipulador de mudança nos inputs
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    // Submeter o formulário
    const handleSubmit = (e) => {
        e.preventDefault();
        setShowConfirmDialog(true);
    };

    // Confirmar criação/edição
    const handleConfirmSubmit = async () => {
        setShowConfirmDialog(false);
        try {
            if (isEditMode) {
                // Atualizar item existente
                await api.put(`/catalogos/${formData.id}`, {
                    codigoNcm: formData.codigoNcm,
                    descricao: formData.descricao,
                    tipo: formData.tipo,
                    unidadeMedida: formData.unidadeMedida,
                });
            } else {
                // Criar novo item
                await api.post("/catalogos", {
                    codigoNcm: formData.codigoNcm,
                    descricao: formData.descricao,
                    tipo: formData.tipo,
                    unidadeMedida: formData.unidadeMedida,
                });
            }

            // Atualizar lista
            fetchCatalogoList();

            // Mostrar sucesso
            setShowSuccessDialog(true);

            // Limpar formulário
            setFormData({
                id: null,
                codigoNcm: "",
                descricao: "",
                tipo: "Material",
                unidadeMedida: "Unidade",
            });
            setIsEditMode(false);
        } catch (error) {
            console.error("Erro ao salvar item:", error);
            alert(
                `Erro ao ${isEditMode ? "atualizar" : "cadastrar"} o item. Verifique os dados e tente novamente.`
            );
        }
    };

    const handleBack = () => {
        navigate('/configuracoes/menus');
    };

    const handleCancelSubmit = () => {
        setShowConfirmDialog(false);
    };

    // Editar item
    const handleEdit = (item) => {
        setIsEditMode(true);
        setFormData({
            id: item.id,
            codigoNcm: item.codigoNcm || "",
            descricao: item.descricao,
            tipo: item.tipo,
            unidadeMedida: item.unidadeMedida,
        });
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    // Deletar item
    const handleDelete = (item) => {
        setItemToDelete(item);
        setShowConfirmDelete(true);
    };

    const handleConfirmDelete = async () => {
        setShowConfirmDelete(false);
        try {
            await api.delete(`/catalogos/${itemToDelete.id}`);
            // Atualizar lista
            fetchCatalogoList();
            setItemToDelete(null);
            setShowSuccessDialog(true);
        } catch (error) {
            console.error("Erro ao deletar item:", error);
            alert("Erro ao deletar o item. Tente novamente.");
        }
    };

    const handleCancelDelete = () => {
        setShowConfirmDelete(false);
        setItemToDelete(null);
    };

    // Cancelar edição
    const handleCancelEdit = () => {
        setIsEditMode(false);
        setFormData({
            id: null,
            codigoNcm: "",
            descricao: "",
            tipo: "Material",
            unidadeMedida: "Unidade",
        });
    };

    return (
        <Template>
            <div className="w-full px-4 py-6 flex flex-col gap-8">
                {/* Título da Página */}
                <div>
                    <h1 className="text-2xl text-stone-800 font-medium">
                        Gerenciar Catálogo de Materiais e Serviços
                    </h1>
                </div>

                {/* Formulário de Criação/Edição */}
                <form
                    onSubmit={handleSubmit}
                    className="flex flex-col gap-4 bg-white p-6 rounded-md border border-slate-200"
                >
                    <div className="text-stone-600 text-base font-medium">
                        {isEditMode ? "Editar Item" : "Cadastrar Novo Item"}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Código NCM */}
                        <div className="flex flex-col gap-1.5">
                            <label className="text-sm text-slate-700 font-medium">
                                Código NCM
                            </label>
                            <input
                                type="text"
                                name="codigoNcm"
                                value={formData.codigoNcm}
                                onChange={handleInputChange}
                                className="px-3 py-2 border border-slate-300 rounded-md text-sm"
                                placeholder="Digite o código NCM (opcional)"
                            />
                        </div>

                        {/* Tipo */}
                        <div className="flex flex-col gap-1.5">
                            <label className="text-sm text-slate-700 font-medium">
                                Tipo
                            </label>
                            <select
                                name="tipo"
                                value={formData.tipo}
                                onChange={handleInputChange}
                                className="px-3 py-2 border border-slate-300 rounded-md text-sm"
                                required
                            >
                                {tipos.map((tipoOption) => (
                                    <option key={tipoOption} value={tipoOption}>
                                        {tipoOption}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Unidade de Medida */}
                        <div className="flex flex-col gap-1.5">
                            <label className="text-sm text-slate-700 font-medium">
                                Unidade de Medida
                            </label>
                            <select
                                name="unidadeMedida"
                                value={formData.unidadeMedida}
                                onChange={handleInputChange}
                                className="px-3 py-2 border border-slate-300 rounded-md text-sm"
                                required
                            >
                                {unidadesMedida.map((unidade) => (
                                    <option key={unidade} value={unidade}>
                                        {unidade}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Descrição */}
                        <div className="flex flex-col gap-1.5">
                            <label className="text-sm text-slate-700 font-medium">
                                Descrição
                            </label>
                            <textarea
                                name="descricao"
                                rows={2}
                                value={formData.descricao}
                                onChange={handleInputChange}
                                className="px-3 py-2 border border-slate-300 rounded-md text-sm resize-none"
                                placeholder="Digite a descrição do item"
                                required
                            />
                        </div>
                    </div>

                    {/* Botões do Formulário */}
                    <div className="flex items-center gap-4">
                        {isEditMode && (
                            <button
                                type="button"
                                onClick={handleCancelEdit}
                                className="px-4 py-2 bg-stone-400 text-white text-sm font-medium rounded-md"
                            >
                                Cancelar
                            </button>
                        )}

                        {podeCriar && (
                            <button
                                type="submit"
                                className="px-4 py-2 bg-stone-600 text-white text-sm font-medium rounded-md"
                            >
                                {isEditMode ? "Salvar Alterações" : "Adicionar Item"}
                            </button>
                        )}
                    </div>
                </form>

                {/* Lista de Itens do Catálogo */}
                <div className="bg-white p-6 rounded-md border border-slate-200">
                    <div className="text-stone-600 text-base font-medium mb-4">
                        Lista de Materiais e Serviços
                    </div>

                    <div className="max-h-[400px] overflow-x-auto">
                        <table className="min-w-full text-left border-collapse">
                            <thead>
                                <tr className="bg-stone-100">
                                    <th className="px-4 py-2 border border-slate-200">ID</th>
                                    <th className="px-4 py-2 border border-slate-200">Código NCM</th>
                                    <th className="px-4 py-2 border border-slate-200">Descrição</th>
                                    <th className="px-4 py-2 border border-slate-200">Tipo</th>
                                    <th className="px-4 py-2 border border-slate-200">Unidade de Medida</th>
                                    <th className="px-4 py-2 border border-slate-200">Ações</th>
                                </tr>
                            </thead>
                            <tbody>
                                {catalogoList.length > 0 ? (
                                    catalogoList.map((item) => (
                                        <tr key={item.id} className="border-b">
                                            <td className="px-4 py-2 border border-slate-200">
                                                {item.id}
                                            </td>
                                            <td className="px-4 py-2 border border-slate-200">
                                                {item.codigoNcm || "-"}
                                            </td>
                                            <td className="px-4 py-2 border border-slate-200">
                                                {item.descricao}
                                            </td>
                                            <td className="px-4 py-2 border border-slate-200">
                                                {item.tipo}
                                            </td>
                                            <td className="px-4 py-2 border border-slate-200">
                                                {item.unidadeMedida}
                                            </td>
                                            <td className="px-4 py-2 border border-slate-200">
                                                {/* Botão Editar */}
                                                {podeAtualizar && (
                                                    <button
                                                        type="button"
                                                        onClick={() => handleEdit(item)}
                                                        className="px-2 py-1 bg-stone-600 text-white rounded-md text-sm mr-2"
                                                    >
                                                        Editar
                                                    </button>
                                                )}
                                                {/* Botão Deletar */}
                                                {podeDeletar && (
                                                    <button
                                                        type="button"
                                                        onClick={() => handleDelete(item)}
                                                        className="px-2 py-1 bg-red-600 text-white rounded-md text-sm"
                                                    >
                                                        Deletar
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td
                                            colSpan={6}
                                            className="px-4 py-2 text-center text-slate-700"
                                        >
                                            Nenhum item cadastrado.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
                {/* Botões de Ação */}
                <div className="flex items-center w-full gap-4">
                    <div className="flex gap-4 ml-auto">
                        <button
                            type="button"
                            className="px-4 py-2 bg-stone-600 text-white text-sm font-medium rounded-md flex items-center gap-2"
                            onClick={handleBack}
                        >
                            <img className="w-4 h-4" src="/images/icon/arrow-left.png" alt="Voltar" />
                            <span>Voltar</span>
                        </button>

                    </div>
                </div>
            </div>

            {/* Modais de Confirmação */}
            <ConfirmationModal
                isVisible={showConfirmDialog}
                onConfirm={handleConfirmSubmit}
                onCancel={handleCancelSubmit}
                message={`Deseja ${isEditMode ? "salvar as alterações" : "adicionar este item"}?`}
            />

            <ConfirmationModal
                isVisible={showConfirmDelete}
                onConfirm={handleConfirmDelete}
                onCancel={handleCancelDelete}
                message={`Deseja deletar o item "${itemToDelete?.descricao}"?`}
                textButtonConfirm="Sim, Deletar"
            />

            {/* Modal de Sucesso */}
            <SuccessModal
                isVisible={showSuccessDialog}
                message={`Item ${isEditMode ? "atualizado" : "adicionado"} com sucesso!`}
                onClose={() => setShowSuccessDialog(false)}
            />
        </Template>
    );
};

export default GerenciarCatalogoMateriaisServicos;
